import { ProductInfoOk, PaymentGatewayMap, PaymentStateMap, SalesContactTypeMap, Discount as PbDiscount } from 'proto/eshop/Basket_pb'

export interface Product extends ProductInfoOk.AsObject {
	id: string
	count: number
}

export interface SelectGridOption {
	value: any
	label: string
	disabled?: boolean
}

export interface PaymentStateObject {
	title: string
	color: string
}

export type PaymentStateObjectMap = {
	[key in ValueOf<PaymentStateMap>]: PaymentStateObject
}

export interface SalesContactObject {
	label: string
	link: string
}

export type SalesContactObjectMap = {
	[key in ValueOf<SalesContactTypeMap>]: SalesContactObject
}

export interface RootState {
	products: Product[],
	userInfo: {
		fullName: string,
		email: string,
		phone: string,
		postcode: string,
		city: string,
		address: string,
		acceptMarketingMessages: boolean,
		isAgree: boolean,
		dntSendTo3rdParties: boolean,
		paymentOption: ValueOf<PaymentGatewayMap>,
		deliveryOption: number,
		deliveryPrice100: number,
		orderComment: string,
	},

	discount: Discount,

	paymentStateValue: keyof PaymentStateObjectMap | null,
	paymentStateMap: PaymentStateObjectMap,
	salesContactTypeMap: SalesContactObjectMap,
}

export class Discount {
	readonly code: string
	readonly target: PbDiscount.TargetMap[keyof PbDiscount.TargetMap]
	readonly percent: number // 0 .. 1
	readonly amount100: number
	readonly productIDs: string[]
	readonly kind: PbDiscount.ValueCase

	constructor() // eslint-disable-line
	constructor(d: PbDiscount) // eslint-disable-line
	constructor(d?: PbDiscount) { // eslint-disable-line
		this.code = d ? d.getCode() : ''
		this.target = d ? d.getTarget() : (PbDiscount.Target.NONE)
		this.percent = d ? d.getPercent() : 0
		this.amount100 = d ? d.getAmount() : 0
		this.productIDs = d ? d.getProductIdsList() : []
		this.kind = d ? d.getValueCase() : (PbDiscount.ValueCase.VALUE_NOT_SET)
	}

	applicableTo(products: Product[]): boolean {
		switch (this.target) {
			case PbDiscount.Target.PRODUCTS_LIST:
				for (let p of products) {
					if (this.productIDs.indexOf(p.id) > -1) {
						return true
					}
				}
				return false
			default:
				return true
		}
	}
}

export class DiscountOp {
	private readonly discount: Discount

	constructor(discount: Discount) {
		this.discount = discount
	}

	get isDiscount(): boolean {
		return this.discount.code !== ''
	}

	apply(price100: number): number {
		switch (this.discount.kind) {
			case PbDiscount.ValueCase.PERCENT:
				return Math.max(price100 - (price100 * this.discount.percent), 0)
			case PbDiscount.ValueCase.AMOUNT:
				return Math.max(price100 - this.discount.amount100, 0)
			default:
				return price100
		}
	}

	computeDeliveryPrice(price100: number): number {
		switch (this.discount.target) {
			case PbDiscount.Target.DELIVERY_ONLY:
				return this.apply(price100)
			default:
				return price100
		}
	}

	computeProductPriceSingle(product: Product): number {
		switch (this.discount.target) {
			case PbDiscount.Target.PRODUCTS_LIST:
				if (this.discount.productIDs.indexOf(product.id) > -1) {
					return this.apply(product.price100)
				}
		}
		return product.price100
	}

	computeProductPriceCount(product: Product): number {
		return this.computeProductPriceSingle(product) * Math.max(product.count, 0)
	}

	isDiscountForProduct(product: Product): boolean {
		switch (this.discount.target) {
			case PbDiscount.Target.PRODUCTS_LIST:
				return this.discount.productIDs.indexOf(product.id) > -1
			default:
				return false
		}
	}

	isDiscountForDelivery(): boolean {
		switch (this.discount.target) {
			case PbDiscount.Target.DELIVERY_ONLY:
				return true
			default:
				return false
		}
	}
}
